import { allocationSignature } from "./allocations";
import type { ExpenseInput, Transaction, TransactionSplit } from "../types";
import { formatMoney, roundMoney } from "./money-math";
import { nameKey } from "./names";

export function transactionSplitMatches(a?: TransactionSplit, b?: TransactionSplit): boolean {
  const typeA = a?.type || "joint";
  const typeB = b?.type || "joint";
  if (typeA !== typeB) return false;
  if (typeA === "joint" || typeA === "equal") return true;
  if (typeA === "personal") {
    const partA = a?.participants?.[0] || "";
    const partB = b?.participants?.[0] || "";
    return partA === partB;
  }
  if (typeA === "custom") {
    const sharesA = a?.shares ? Object.entries(a.shares).sort(([k1], [k2]) => k1.localeCompare(k2)) : [];
    const sharesB = b?.shares ? Object.entries(b.shares).sort(([k1], [k2]) => k1.localeCompare(k2)) : [];
    return JSON.stringify(sharesA) === JSON.stringify(sharesB);
  }
  return true;
}

/**
 * Checks whether an existing ledger transaction already matches an expense input.
 * Used before saves to avoid unnecessary writes, and by Moo proposal execution to verify
 * that reviewed transactions have not changed.
 */
export function transactionMatches(tx: Transaction, input: ExpenseInput): boolean {
  if (input.conversionTransactionId !== undefined && (tx.currencyConversion?.counterpart.id || "") !== input.conversionTransactionId) return false;
  if (input.goalId !== undefined && (tx.goalId || "") !== input.goalId) return false;
  if (input.completeGoal && !tx.goalCompleted) return false;
  if (input.type && input.type !== (tx.toAccountId ? "Transfer" : tx.type)) return false;
  const confirmingDraft = !input.payeeId && !!input.draftPayeeName?.trim();
  const draftSaved = confirmingDraft && !!tx.payeeId &&
    nameKey(tx.payeeName || "") === nameKey(input.draftPayeeName || "");

  if (input.type === "Transfer") {
    const rate = Number(input.exchangeRate || 1);
    const expectedReceived = roundMoney(Number(input.amount) * rate);
    return (
      tx.accountId === input.accountId &&
      tx.toAccountId === input.toAccountId &&
      tx.amount === Number(input.amount) &&
      (tx.receivedAmount === undefined || tx.receivedAmount === expectedReceived) &&
      tx.date.slice(0, 10) === input.date &&
      tx.notes === input.notes &&
      (input.time === undefined || (tx.time || "") === input.time) &&
      (input.tripId === undefined || (tx.tripId || "") === (input.tripId || "")) &&
      (input.tripDestination === undefined || (tx.tripDestination || "") === (input.tripDestination || "")) &&
      (input.isSettlement === undefined || !!tx.isSettlement === !!input.isSettlement) &&
      (input.pending === undefined || !!tx.pending === input.pending) &&
      (input.needsReview === undefined || !!tx.needsReview === input.needsReview)
    );
  }

  const desired = {
    ...tx,
    category: input.category,
    subcategory: input.subcategory,
    amount: Number(input.amount),
    reportableAmount: Number(input.reportableAmount ?? input.amount),
    allocations: input.allocations?.map((p) => ({
      ...p,
      amount: Number(p.amount),
      reportableAmount: Number(p.reportableAmount ?? p.amount),
      amountBase: null,
      reportableAmountBase: null,
    })),
  };

  return (
    allocationSignature(tx) === allocationSignature(desired) &&
    tx.accountId === input.accountId &&
    tx.amount === Number(input.amount) &&
    tx.date.slice(0, 10) === input.date &&
    tx.category === input.category &&
    tx.subcategory === input.subcategory &&
    (tx.title || tx.description) === input.title &&
    tx.notes === input.notes &&
    (draftSaved || input.payeeId === undefined || (tx.payeeId || "") === input.payeeId) &&
    (confirmingDraft ? draftSaved : input.draftPayeeName === undefined || (tx.draftPayeeName || "") === "") &&
    (input.markup === undefined || tx.markup === (input.markup ? `${formatMoney(Number(input.markup))}%` : "")) &&
    (input.time === undefined || (tx.time || "") === input.time) &&
    (input.pending === undefined || !!tx.pending === input.pending) &&
    (input.needsReview === undefined || !!tx.needsReview === input.needsReview) &&
    tx.reportableAmount === Number(input.reportableAmount ?? input.amount) &&
    (input.tripId === undefined || (tx.tripId || "") === (input.tripId || "")) &&
    (input.tripDestination === undefined || (tx.tripDestination || "") === (input.tripDestination || "")) &&
    (input.split === undefined || transactionSplitMatches(tx.split, input.split))
  );
}
